import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isPatientProfile } from './patientRole';

/**
 * Patient-portal route guard (issue #154). ROLE/UI isolation lives here; DATA
 * isolation is the server-side AccessPolicy (`patientAccessPolicy`).
 *
 * - While the Medplum session resolves → render nothing.
 * - No session → bounce to the portal sign-in (preserving intended location).
 * - A NON-patient session (e.g. a clinician who is signed in) is rejected: it is
 *   sent to the clinician app root so a clinician can never land in patient UI,
 *   and — combined with the clinician `RequireAuth` bouncing Patient profiles the
 *   other way — the two spaces never cross.
 * - A Patient session → render the portal shell + feature routes.
 */
export function RequirePatientAuth() {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();

  if (medplum.isLoading()) {
    return <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }

  if (!profile) {
    return <Navigate to="/portal/signin" replace state={{ from: location }} />;
  }

  if (!isPatientProfile(profile)) {
    // A signed-in clinician (or other role) must not see patient UI. Send them to
    // the clinician app, which owns their session.
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
